import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=user
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState(localStorage.getItem("si_ws") || null);

  const loadWorkspaces = useCallback(async () => {
    const { data } = await api.get("/workspaces");
    setWorkspaces(data);
    setWorkspaceId((cur) => {
      const serverDefault = data.find((w) => w.is_default)?.id || null;
      // Adopt the owner's chosen default once per default-change, on every device.
      // After that, manual per-device switches (stored in si_ws) are respected.
      if (serverDefault && localStorage.getItem("si_ws_default") !== serverDefault) {
        localStorage.setItem("si_ws_default", serverDefault);
        localStorage.setItem("si_ws", serverDefault);
        return serverDefault;
      }
      if (cur && data.some((w) => w.id === cur)) return cur;
      const fallback = serverDefault || data[0]?.id || null;
      if (fallback) localStorage.setItem("si_ws", fallback);
      return fallback;
    });
    return data;
  }, []);

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadWorkspaces();
    } catch {
      setUser(false);
    }
  }, [loadWorkspaces]);

  useEffect(() => { checkSession(); }, [checkSession]);

  // When the API layer determines the session is truly over (refresh failed),
  // drop to the signed-out state so the app routes to /login. Any in-progress
  // draft is stashed by the editor before this fires, and restored after sign-in.
  useEffect(() => {
    const onExpired = () => { setUser(false); setWorkspaces([]); };
    window.addEventListener("si:session-expired", onExpired);
    return () => window.removeEventListener("si:session-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    await loadWorkspaces();
    return data;
  };
  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    await loadWorkspaces();
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setWorkspaces([]);
  };
  const selectWorkspace = (id) => {
    localStorage.setItem("si_ws", id);
    setWorkspaceId(id);
  };
  const setDefaultWorkspace = async (id) => {
    await api.put(`/workspaces/${id}/default`);
    localStorage.setItem("si_ws_default", id);
    await loadWorkspaces();
  };

  const currentWorkspace = workspaces.find((w) => w.id === workspaceId) || null;
  const role = currentWorkspace?.role || "viewer";
  const canEdit = role === "owner" || role === "editor";
  const isOwner = role === "owner";

  return (
    <AuthContext.Provider value={{
      user, workspaces, workspaceId, currentWorkspace, role, canEdit, isOwner,
      login, register, logout, selectWorkspace, setDefaultWorkspace, loadWorkspaces, checkSession, apiError,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
